import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner
} from 'reactstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import {
  getRecipientesList,
  postRecipientes,
  deleteRecipientes,
  getRecipientesFilter,
  patchRecipientesUpdate
} from '../../../helpers/backend';
import Pagination from '../../../components/Common/Pagination_ansync';

const Recipientes = () => {
  const [params, setParams] = useState({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editingRecipiente, setEditingRecipiente] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    dimensiones: '',
    factor: '',
    volumen_calculado: ''
  });

  // Opciones predefinidas para unidad de medida (no se usan más pero se mantienen por compatibilidad)
  const unidadMedidaOptions = [
    'litros',
    'galones',
    'metros cúbicos',
    'kilogramos',
    'toneladas'
  ];

  // Efecto inicial para cargar datos
  useEffect(() => {
    fetchRecipientes();
  }, []);

  // Efecto para manejar la búsqueda con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        getFilterData(`?search=${searchTerm}`);
      } else {
        fetchRecipientes();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchRecipientes = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        limit: 30
      };
      const response = await getRecipientesList(params);
      setParams({
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
        results: response.data.results || [],
      });
    } catch (error) {
      showAlert('Error al cargar los recipientes', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setEditingRecipiente(null);
      setFormData({
        codigo: '',
        nombre: '',
        descripcion: '',
        dimensiones: '',
        factor: '',
        volumen_calculado: ''
      });
    }
  };

  const handleEdit = (recipiente) => {
    setEditingRecipiente(recipiente);
    setFormData({
      codigo: recipiente.codigo,
      nombre: recipiente.nombre,
      descripcion: recipiente.descripcion,
      dimensiones: recipiente.dimensiones,
      factor: recipiente.factor,
      volumen_calculado: recipiente.volumen_calculado
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        codigo: formData.codigo,
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        dimensiones: formData.dimensiones,
        factor: parseFloat(formData.factor),
        volumen_calculado: parseFloat(formData.volumen_calculado)
      };

      if (editingRecipiente) {
        // Actualizar recipiente existente
        await patchRecipientesUpdate(editingRecipiente.id, dataToSend);
        showAlert('Recipiente actualizado exitosamente', 'success');
      } else {
        await postRecipientes(dataToSend);
        showAlert('Recipiente creado exitosamente', 'success');
      }
      toggleModal();
      fetchRecipientes();
    } catch (error) {
      showAlert('Error al guardar el recipiente', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este recipiente?')) {
      try {
        await deleteRecipientes(id);
        showAlert('Recipiente eliminado exitosamente', 'success');
        fetchRecipientes();
      } catch (error) {
        showAlert('Error al eliminar el recipiente', 'danger');
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const getFilterData = async (url) => {
    setLoading(true);
    
    try {
      let finalUrl = '';
      
      if (url && url.startsWith('http')) {
        // Es una URL completa del backend (next/previous)
        const urlObj = new URL(url);
        finalUrl = urlObj.search; // Solo los parámetros de consulta
      } else {
        // Es una URL relativa o parámetros de consulta
        if (url && !url.startsWith('?') && url.length > 0) {
          finalUrl = '?' + url;
        } else {
          finalUrl = url || '';
        }
        
        // Mantener filtros de búsqueda si existen
        const urlParams = new URLSearchParams(finalUrl.startsWith('?') ? finalUrl.slice(1) : finalUrl);
        
        if (searchTerm && !urlParams.has('search')) {
          urlParams.set('search', searchTerm);
        }
        
        finalUrl = urlParams.toString() ? `?${urlParams.toString()}` : '';
      }
      
      console.log('URL final para getRecipientesList:', finalUrl);
      const response = await getRecipientesFilter(finalUrl);
      setParams({
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
        results: response.data.results || [],
      });
      console.log('Recipientes cargados:', response.data);
    } catch (error) {
      console.error('Error al cargar recipientes:', error);
      showAlert('Error al cargar los recipientes', 'danger');
    }
    
    setLoading(false);
  };

  return (
    <Container fluid>
      <div className='page-content'>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">Gestión de Recipientes</h4>
                  <Button color="primary" onClick={toggleModal}>
                    <FaPlus className="me-2" />
                    Nuevo Recipiente
                  </Button>
                </div>

                {alert.show && (
                  <Alert color={alert.type} className="mb-3">
                    {alert.message}
                  </Alert>
                )}

                <Row className="mb-3">
                  <Col md={6}>
                    <div className="position-relative">
                      <Input
                        type="text"
                        placeholder="Buscar recipientes..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="ps-5"
                      />
                      <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                    </div>
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                    <p className="mt-2">Cargando recipientes...</p>
                  </div>
                ) : (
                  <>
                    <Table responsive striped>
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Nombre</th>
                          <th>Descripción</th>
                          <th>Dimensiones</th>
                          <th>Factor</th>
                          <th>Volumen Calculado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {params.results.length > 0 ? (
                          params.results.map((recipiente) => (
                            <tr key={recipiente.id}>
                              <td>{recipiente.codigo}</td>
                              <td>{recipiente.nombre}</td>
                              <td>{recipiente.descripcion}</td>
                              <td>{recipiente.dimensiones}</td>
                              <td>{recipiente.factor}</td>
                              <td>{recipiente.volumen_calculado}</td>
                              <td>
                                <Button
                                  color="info"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleEdit(recipiente)}
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(recipiente.id)}
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center">
                              No se encontraron recipientes
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>

                    <Pagination
                      count={params.count}
                      next={params.next}
                      previous={params.previous}
                      limit={30}
                      filter={true}
                      baseUrl="/casos/parametrizacion-recipientes/"
                      filterFunction={getFilterData}

                    />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>


      {/* Modal para crear/editar recipiente */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          {editingRecipiente ? 'Editar Recipiente' : 'Nuevo Recipiente'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="codigo">Código *</Label>
                  <Input
                    type="text"
                    id="codigo"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="nombre">Nombre *</Label>
                  <Input
                    type="text"
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="dimensiones">Dimensiones</Label>
                  <Input
                    type="text"
                    id="dimensiones"
                    value={formData.dimensiones}
                    onChange={(e) => setFormData({ ...formData, dimensiones: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="factor">Factor *</Label>
                  <Input
                    type="number"
                    id="factor"
                    step="0.001"
                    value={formData.factor}
                    onChange={(e) => setFormData({ ...formData, factor: e.target.value })}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="volumen_calculado">Volumen Calculado</Label>
                  <Input
                    type="number"
                    id="volumen_calculado"
                    step="0.001"
                    value={formData.volumen_calculado}
                    onChange={(e) => setFormData({ ...formData, volumen_calculado: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup>
                  <Label for="descripcion">Descripción</Label>
                  <Input
                    type="textarea"
                    id="descripcion"
                    rows="3"
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Cancelar
            </Button>
            <Button color="primary" type="submit">
              {editingRecipiente ? 'Actualizar' : 'Crear'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </Container>
  );
};

export default Recipientes;